import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft, BookOpen, Calculator, Pencil, Lock, CheckCircle2,
  Play, Trophy, Target, Clock, ChevronRight, Star, Zap,
  BarChart3, Calendar, Award, Flame, Sparkles
} from 'lucide-react';
import useSubscriptionAccess from '@/hooks/useSubscriptionAccess';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Band colors and info
const BAND_CONFIG = {
  A: { color: 'from-orange-500 to-red-500', bg: 'bg-orange-500/20', text: 'text-orange-600', label: 'Foundation' },
  B: { color: 'from-blue-500 to-cyan-500', bg: 'bg-blue-500/20', text: 'text-blue-600', label: 'Developing' },
  C: { color: 'from-green-500 to-emerald-500', bg: 'bg-green-500/20', text: 'text-green-600', label: 'Secure' },
  D: { color: 'from-purple-500 to-pink-500', bg: 'bg-purple-500/20', text: 'text-purple-600', label: 'Mastery' }
};

const AdaptiveLearning = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const { access, loading: accessLoading, has } = useSubscriptionAccess();
  const hasAdaptive = has('adaptive');

  // Detect user stage. The 16-week curriculum is currently 11+-specific
  // (Bands A-E based on KS2 maths). For Year 9-11 / GCSE / A-Level students
  // we render a "Coming Soon" preview rather than the wrong-stage content.
  const _user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const _yg = (_user.year_group || _user.yearGroup || _user.studentProfile?.yearGroup || '').toLowerCase();
  const _ut = (_user.user_type || _user.userType || '').toLowerCase();
  const isElevenPlusStage = /year[- ]?(5|6)/i.test(_yg) || _ut.includes('11+') || _ut.includes('eleven') || _ut.includes('ks2');
  const isSevenPlusStage  = /year[- ]?(1|2)/i.test(_yg) || _ut.includes('7+') || _ut.includes('seven') || _ut.includes('ks1');
  const isAdaptiveSupportedStage = isSevenPlusStage || isElevenPlusStage || (!_yg && !_ut); // unknown stage → fall through to capability gate

  const [loading, setLoading] = useState(true);
  const [currentBand, setCurrentBand] = useState('B');
  const [bandInfo, setBandInfo] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [weekData, setWeekData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('maths');

  useEffect(() => {
    // Skip the gated fetches when the user doesn't have adaptive access OR
    // their stage isn't 11+/7+. Backend would 403 anyway.
    if (!accessLoading && (!hasAdaptive || !isAdaptiveSupportedStage)) {
      setLoading(false);
      return;
    }
    if (hasAdaptive && isAdaptiveSupportedStage) {
      fetchData();
    }
  }, [currentWeek, hasAdaptive, accessLoading, isAdaptiveSupportedStage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch band info
      const bandRes = await axios.get(`${API}/adaptive/band`, { headers });
      setCurrentBand(bandRes.data.current_band || 'B');
      setBandInfo(bandRes.data.band_info);
      
      // Fetch current week curriculum
      const weekRes = await axios.get(`${API}/adaptive/curriculum/week/${currentWeek}`, { headers });
      setWeekData(weekRes.data);
      
      // Fetch progress
      const progressRes = await axios.get(`${API}/adaptive/progress`, { headers });
      setProgress(progressRes.data);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load curriculum');
    } finally {
      setLoading(false);
    }
  };

  const startWorksheet = (worksheetId) => {
    navigate(`/adaptive/worksheet/${worksheetId}`);
  };

  const bandConfig = BAND_CONFIG[currentBand] || BAND_CONFIG.B;

  if (loading || accessLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Loading your learning path...</p>
        </div>
      </div>
    );
  }

  // ===== Stage Gate =====
  // For users whose stage isn't supported by the 11+ curriculum (Year 9-13),
  // show a tailored "Coming Soon for your stage" preview rather than the
  // wrong content. We check this BEFORE the capability lock so a paying GCSE
  // user still sees the right message.
  if (!isAdaptiveSupportedStage) {
    return <AdaptiveLearningStageGate navigate={navigate} yearGroup={_yg} />;
  }

  // ===== Locked Preview Mode =====
  // For users without `has_adaptive` (Beginner / Free / None), render a marketing
  // preview of the page so they can see what they'd unlock by upgrading.
  if (!hasAdaptive) {
    return <AdaptiveLearningLocked navigate={navigate} access={access} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white" data-testid="adaptive-learning-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="text-slate-500 hover:text-slate-900"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">16-Week Learning Path</h1>
                <p className="text-sm text-slate-500">Year 5-6 • 11+ Preparation</p>
              </div>
            </div>
            
            {/* Band Badge */}
            <div className={`px-4 py-2 rounded-xl ${bandConfig.bg} border border-slate-200/50`}>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${bandConfig.color}`}></div>
                <span className={`font-semibold ${bandConfig.text}`}>Band {currentBand}</span>
                <span className="text-slate-500 text-sm">• {bandConfig.label}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-24">
        {/* Progress Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="bg-white border-slate-200/50">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${bandConfig.color} flex items-center justify-center`}>
                    <Trophy className="w-8 h-8 text-slate-900" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Week {currentWeek}</h2>
                    <p className="text-slate-500">{weekData?.title}</p>
                  </div>
                </div>
                
                <div className="flex-1 max-w-md">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-500">Overall Progress</span>
                    <span className="text-slate-900 font-medium">{progress?.overall_progress || 0}%</span>
                  </div>
                  <Progress value={progress?.overall_progress || 0} className="h-3" />
                </div>
                
                <div className="flex gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-900">{progress?.weeks_completed || 0}</div>
                    <div className="text-xs text-slate-500">Weeks Done</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{currentWeek}/16</div>
                    <div className="text-xs text-slate-500">Current Week</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Adaptive Mock Engine + Weekly Mock CTA — moved from main nav (Advance / Pro tier feature) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-8 grid sm:grid-cols-2 gap-4"
          data-testid="adaptive-mock-shortcuts"
        >
          <button
            onClick={() => navigate('/adaptive-mock')}
            className="group text-left rounded-2xl border border-pink-500/30 bg-gradient-to-br from-pink-500/15 via-rose-500/10 to-rose-500/5 hover:from-pink-500/25 hover:to-rose-500/15 p-5 transition-all hover:-translate-y-0.5"
            data-testid="open-weekly-mock-btn"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-slate-900" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-slate-900">Weekly Mock</h3>
                  <Badge className="bg-rose-500/20 text-rose-600 border-rose-500/30 text-[10px]">Advance / Pro</Badge>
                </div>
                <p className="text-sm text-slate-500">Take a fresh weekly mock paper tailored to your current band.</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-slate-900 group-hover:translate-x-1 transition-all flex-shrink-0 mt-2" />
            </div>
          </button>

          <button
            onClick={() => navigate('/adaptive-mock')}
            className="group text-left rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-500/15 via-indigo-500/10 to-indigo-500/5 hover:from-purple-500/25 hover:to-indigo-500/15 p-5 transition-all hover:-translate-y-0.5"
            data-testid="open-adaptive-mock-engine-btn"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-slate-900" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-slate-900">Adaptive Mock Engine</h3>
                  <Badge className="bg-purple-500/20 text-purple-600 border-purple-500/30 text-[10px]">Advance / Pro</Badge>
                </div>
                <p className="text-sm text-slate-500">Auto-generated mocks that target your weakest skills first.</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-slate-900 group-hover:translate-x-1 transition-all flex-shrink-0 mt-2" />
            </div>
          </button>
        </motion.div>

        {/* Week Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map((week) => (
            <Button
              key={week}
              variant={currentWeek === week ? "default" : "outline"}
              size="sm"
              onClick={() => setCurrentWeek(week)}
              className={`min-w-[60px] ${
                currentWeek === week 
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600' 
                  : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`week-${week}-btn`}
            >
              W{week}
            </Button>
          ))}
        </div>

        {/* Subject Tabs */}
        <div className="flex gap-4 mb-6">
          <Button
            variant={selectedSubject === 'maths' ? 'default' : 'outline'}
            onClick={() => setSelectedSubject('maths')}
            className={selectedSubject === 'maths' 
              ? 'bg-gradient-to-r from-blue-600 to-cyan-600' 
              : 'bg-slate-100 border-slate-200'
            }
            data-testid="maths-tab"
          >
            <Calculator className="w-4 h-4 mr-2" />
            Maths
          </Button>
          <Button
            variant={selectedSubject === 'english' ? 'default' : 'outline'}
            onClick={() => setSelectedSubject('english')}
            className={selectedSubject === 'english' 
              ? 'bg-gradient-to-r from-green-600 to-emerald-600' 
              : 'bg-slate-100 border-slate-200'
            }
            data-testid="english-tab"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            English
          </Button>
        </div>

        {/* Week Focus Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card className="bg-white border-slate-200/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${selectedSubject === 'maths' ? 'bg-blue-500/20' : 'bg-green-500/20'}`}>
                  {selectedSubject === 'maths' 
                    ? <Calculator className="w-6 h-6 text-blue-600" />
                    : <BookOpen className="w-6 h-6 text-green-600" />
                  }
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">
                    {selectedSubject === 'maths' ? weekData?.maths_focus : weekData?.english_focus}
                  </h3>
                  <p className="text-slate-500 text-sm mb-3">
                    Band {currentBand} Focus: {weekData?.adaptive_focus}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="bg-slate-100 text-slate-600">
                      <Clock className="w-3 h-3 mr-1" /> 5 Worksheets
                    </Badge>
                    <Badge variant="outline" className="bg-slate-100 text-slate-600">
                      <Target className="w-3 h-3 mr-1" /> Adaptive Content
                    </Badge>
                    {weekData?.has_mock && (
                      <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">
                        <Award className="w-3 h-3 mr-1" /> Mock Week
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Worksheets Grid */}
        <div className="grid gap-4">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Pencil className="w-5 h-5 text-purple-600" />
            {selectedSubject === 'maths' ? 'Maths' : 'English'} Worksheets
          </h3>
          
          {weekData?.worksheets?.[selectedSubject]?.map((worksheetId, index) => (
            <motion.div
              key={worksheetId}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <WorksheetCard
                worksheetId={worksheetId}
                index={index + 1}
                subject={selectedSubject}
                onStart={() => startWorksheet(worksheetId)}
                headers={headers}
              />
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
};

// Worksheet Card Component
const WorksheetCard = ({ worksheetId, index, subject, onStart, headers }) => {
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWorksheet = async () => {
      try {
        const res = await axios.get(`${API}/adaptive/worksheet/${worksheetId}`, { headers });
        setWorksheet(res.data);
      } catch (error) {
        console.error('Error fetching worksheet:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWorksheet();
  }, [worksheetId]);

  if (loading) {
    return (
      <Card className="bg-slate-100 border-slate-200/50 animate-pulse">
        <CardContent className="p-4">
          <div className="h-16"></div>
        </CardContent>
      </Card>
    );
  }

  const subjectColors = {
    maths: 'from-blue-500 to-cyan-500',
    english: 'from-green-500 to-emerald-500'
  };

  return (
    <Card 
      className="bg-slate-100 border-slate-200/50 hover:bg-slate-100 transition-all cursor-pointer group"
      onClick={onStart}
      data-testid={`worksheet-card-${worksheetId}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${subjectColors[subject]} flex items-center justify-center text-white font-bold`}>
            {subject === 'maths' ? 'M' : 'E'}{index}
          </div>
          
          <div className="flex-1">
            <h4 className="font-semibold text-slate-900 group-hover:text-purple-600 transition-colors">
              {worksheet?.title || `Worksheet ${index}`}
            </h4>
            <p className="text-sm text-slate-500">
              {worksheet?.total_questions} questions • {worksheet?.total_marks} marks
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-slate-100 text-slate-600">
              <Clock className="w-3 h-3 mr-1" />
              {worksheet?.time_minutes || 15} min
            </Badge>
            <Button 
              size="sm" 
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            >
              <Play className="w-4 h-4 mr-1" />
              Start
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdaptiveLearning;

// ===== Stage Gate =====
// Shown to Year 9-13 / GCSE / A-Level / unknown-stage users when the
// adaptive curriculum content for their stage isn't built yet.
// (The current 16-week dataset is 11+-specific — Bands A-E based on KS2 maths.)
const AdaptiveLearningStageGate = ({ navigate, yearGroup }) => {
  const stageLabel = (() => {
    if (/year[- ]?(9|10|11)/i.test(yearGroup) || yearGroup.includes('gcse') || yearGroup.includes('ks4')) return 'GCSE (Year 9-11)';
    if (/year[- ]?(12|13)/i.test(yearGroup) || yearGroup.includes('a-level') || yearGroup.includes('alevel')) return 'A-Levels (Year 12-13)';
    if (/year[- ]?(7|8)/i.test(yearGroup) || yearGroup.includes('ks3')) return 'KS3 (Year 7-8)';
    return 'your stage';
  })();
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white" data-testid="adaptive-stage-gate">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Adaptive Learning</h1>
            <p className="text-sm text-slate-500">{stageLabel} · Coming soon</p>
          </div>
          <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">
            <Lock className="w-3 h-3 mr-1" /> Coming soon
          </Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 pb-24 space-y-6">
        <Card className="bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-rose-500/10 border border-amber-500/30">
          <CardContent className="p-8 md:p-10 text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-slate-900" />
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Adaptive Learning for {stageLabel} is coming soon</h2>
            <p className="text-slate-600 text-sm md:text-base">
              The current 16-week adaptive curriculum is built around the 11+ exam (Year 5-6).
              We're putting together a stage-appropriate adaptive path for you — meanwhile, your
              practice papers, mock papers and resource hub are fully tailored to your year group.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Button onClick={() => navigate('/categories')} className="bg-white text-slate-900 hover:bg-slate-100" data-testid="explore-other-features-btn">
                Explore other features
              </Button>
              <Button onClick={() => navigate('/ai-practice')} variant="outline" className="border-slate-200 text-slate-900 hover:bg-slate-100" data-testid="go-to-practice-btn">
                Go to Practice Papers <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ===== Locked Preview =====
// Shown to users who don't have `has_adaptive` (Beginner / Free / None).
// The page is browseable so users can SEE what's locked behind Advance/Pro
// rather than being bounced to a generic upgrade screen.
const AdaptiveLearningLocked = ({ navigate, access }) => {
  const tierLabel = access?.tier_label || 'your current plan';
  const goUpgrade = () => navigate('/subscription');

  const sampleWeeks = [
    { n: 1, title: 'Number Sense & Reading Basics' },
    { n: 2, title: 'Fractions Foundations' },
    { n: 3, title: 'Comprehension Skills' },
    { n: 4, title: 'Word Problems & Vocabulary' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white" data-testid="adaptive-learning-locked">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">16-Week Learning Path</h1>
            <p className="text-sm text-slate-500">Preview · Locked on {tierLabel}</p>
          </div>
          <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">
            <Lock className="w-3 h-3 mr-1" /> Preview
          </Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-24 space-y-8">
        {/* Upgrade banner */}
        <Card className="bg-gradient-to-r from-purple-600/20 via-fuchsia-600/15 to-indigo-600/20 border border-purple-500/40" data-testid="upgrade-banner">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-slate-900">Unlock Adaptive Learning</h2>
                  <p className="text-slate-600 text-sm md:text-base mt-1">
                    Get a personalised 16-week study plan, weekly mocks, and an AI mock engine that adapts to your weak spots.
                  </p>
                </div>
              </div>
              <Button
                onClick={goUpgrade}
                className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white h-11 px-6 font-semibold whitespace-nowrap"
                data-testid="upgrade-to-advance-btn"
              >
                Upgrade to Advance <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Locked Mock Engine + Weekly Mock cards */}
        <div className="grid sm:grid-cols-2 gap-4" data-testid="locked-shortcuts">
          {[
            { title: 'Weekly Mock', desc: 'A fresh weekly mock paper tailored to your current band.', Icon: Target, gradient: 'from-pink-500 to-rose-500', testId: 'locked-weekly-mock' },
            { title: 'Adaptive Mock Engine', desc: 'Auto-generated mocks that target your weakest skills first.', Icon: Zap, gradient: 'from-purple-500 to-indigo-500', testId: 'locked-adaptive-mock-engine' },
          ].map(({ title, desc, Icon, gradient, testId }) => (
            <button
              key={title}
              onClick={goUpgrade}
              className="group relative text-left rounded-2xl border border-slate-200/60 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-purple-500/50 overflow-hidden"
              data-testid={testId}
            >
              <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px]" aria-hidden />
              <div className="relative flex items-start gap-4 opacity-80">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 grayscale-[40%]`}>
                  <Icon className="w-6 h-6 text-slate-900" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900">{title}</h3>
                    <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30 text-[10px]">
                      <Lock className="w-2.5 h-2.5 mr-1" /> Advance / Pro
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500">{desc}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-slate-900 group-hover:translate-x-1 transition-all flex-shrink-0 mt-2" />
              </div>
            </button>
          ))}
        </div>

        {/* Sample 16-week path preview */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            What's inside the 16-Week Path
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {sampleWeeks.map((w) => (
              <div
                key={w.n}
                className="rounded-xl border border-slate-200/60 bg-white p-4 flex items-center gap-3"
                data-testid={`locked-week-${w.n}`}
              >
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">
                  W{w.n}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">Week {w.n} · {w.title}</p>
                  <p className="text-xs text-slate-500">5 worksheets · adaptive content</p>
                </div>
                <Lock className="w-4 h-4 text-slate-500 flex-shrink-0" />
              </div>
            ))}
            <div className="sm:col-span-2 text-center text-xs text-slate-500 pt-2">
              + 12 more weeks · 80+ adaptive worksheets · Maths &amp; English
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <Card className="bg-white border-slate-200/50">
          <CardContent className="p-6 md:p-8 text-center space-y-3">
            <p className="text-slate-600 text-sm md:text-base">
              Adaptive Learning, Weekly Mocks and the Adaptive Mock Engine are unlocked on the
              <span className="text-slate-900 font-semibold"> Advance</span> and
              <span className="text-slate-900 font-semibold"> Pro</span> tiers.
            </p>
            <Button
              onClick={goUpgrade}
              className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white h-11 px-6"
              data-testid="bottom-upgrade-btn"
            >
              See plans <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
